import type { ChatSubagentCard } from "../ui-types.ts";
import { isFleetCompletionMessage } from "./chat-assist-display.ts";
import { formatChangedPathsMarkdown } from "./fleet-subagent-changed-paths.ts";
import { filterSubagentRunCards, subagentRunTitle } from "./subagent-run-display.ts";
import {
  fleetSummaryHeadline,
  resolveSubagentActivityPhase,
  subagentPhaseLabel,
  summarizeSubagentFleet,
} from "./subagent-run-status.ts";

export type FleetCompletionNoticeHost = {
  sessionKey: string;
  chatRunId: string | null;
  chatSending: boolean;
  chatSubagents: ChatSubagentCard[];
  chatMessages?: unknown[];
  orchestrationReportsDone: Record<string, boolean>;
  fleetCompletionNotifiedFingerprint: Record<string, string>;
};

/** Skip re-injecting fleet-complete notices already present in the loaded transcript. */
export function hasFleetCompletionInTranscript(messages: unknown[] | undefined): boolean {
  if (!messages?.length) {
    return false;
  }
  const tail = Math.max(0, messages.length - 40);
  for (let i = messages.length - 1; i >= tail; i -= 1) {
    if (isFleetCompletionMessage(messages[i])) {
      return true;
    }
  }
  return false;
}

export function isChatExecutionIdle(
  host: Pick<FleetCompletionNoticeHost, "chatRunId" | "chatSending">,
): boolean {
  return !host.chatSending && !host.chatRunId;
}

/**
 * Stable key for a settled fleet snapshot (per chat session).
 * Uses id+status only — path enrichment / updatedAt churn must not re-trigger inject.
 */
export function fleetCompletionFingerprint(cards: ChatSubagentCard[]): string {
  return filterSubagentRunCards(cards)
    .map((c) => `${c.id}:${c.status}`)
    .sort()
    .join("|");
}

/**
 * Orchestration fan-out gets a detailed transcript note via `maybePersistOrchestrationReports`.
 * Skip the short fleet-complete notice for orchestration-only runs (before or after that report).
 */
/** Plan units use labels like `46694374:Coverage audit`. */
const PLAN_UNIT_LABEL_RE = /^[0-9a-f]{8}:/i;

export function orchestrationIdFromPlanUnitLabel(label: string | undefined): string | undefined {
  const trimmed = label?.trim() ?? "";
  const match = PLAN_UNIT_LABEL_RE.exec(trimmed);
  if (!match) {
    return undefined;
  }
  return `fleet-plan:${match[0].slice(0, -1)}`;
}

export function shouldSkipFleetCompletionForOrchestration(
  cards: ChatSubagentCard[],
  _orchestrationReportsDone: Record<string, boolean>,
): boolean {
  const runCards = filterSubagentRunCards(cards);
  if (runCards.length === 0) {
    return true;
  }
  const standalone = runCards.filter((c) => {
    if (c.orchestrationId?.trim()) {
      return false;
    }
    return !PLAN_UNIT_LABEL_RE.test(c.label?.trim() ?? "");
  });
  return standalone.length === 0;
}

export function buildFleetCompletionNoticeMessage(cards: ChatSubagentCard[]): string {
  const runCards = filterSubagentRunCards(cards);
  const summary = summarizeSubagentFleet(runCards);
  const headline = fleetSummaryHeadline(summary);
  const lines = runCards.flatMap((c) => {
    const phase = resolveSubagentActivityPhase(c);
    // JAISIU-3319 / Pillar H: a cancelled card is no longer just "done"
    // surfacing as "Cancelled" preserves that the operator intervened;
    // operators asking "did my cancel work?" rely on this distinct label.
    let status: string;
    if (c.status === "cancelled") {
      status = "Cancelled";
    } else {
      status = subagentPhaseLabel(phase);
    }
    const head = `- **${subagentRunTitle(c)}** — ${status}`;
    const meta: string[] = [];
    // JAISIU-3319 / Pillar H: enrich the per-task line with duration + model
    // so the parent can tell at a glance which worker was on which model
    // and how long it actually ran. Without this the operator has to open
    // each child card to find out.
    const duration = formatSubagentDurationMs(c);
    if (duration) {
      meta.push(duration);
    }
    if (typeof c.model === "string" && c.model.trim()) {
      meta.push(`model \`${c.model.trim()}\``);
    }
    const errorText = c.error?.trim();
    if (errorText) {
      meta.push(`error: ${errorText}`);
    }
    const metaLine = meta.length > 0 ? [head, `  ${meta.join(" · ")}`] : [head];
    const pathLines = formatChangedPathsMarkdown(c.changedPaths ?? []);
    return [...metaLine, ...pathLines];
  });
  if (lines.length === 0) {
    return `**${headline}**`;
  }
  return [`**${headline}**`, "", ...lines].join("\n");
}

/**
 * JAISIU-3319 / Pillar H: human-readable duration for a settled card.
 * Returns undefined while the run is still live (no `endedAt` upstream —
 * the runtime stamps it on settle). Falls back to the
 * `updatedAt − createdAt` gap when only one side is available, which is
 * what transient-streamed cards look like during the dedupe window.
 */
function formatSubagentDurationMs(c: ChatSubagentCard): string | undefined {
  const updated = c.updatedAt;
  const created = c.createdAt;
  if (typeof updated !== "number" || typeof created !== "number") return undefined;
  const ms = Math.max(0, updated - created);
  if (ms < 1000) return undefined;
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

/**
 * JAISIU-3319 / Pillar H: pick the child card that the operator should be
 * auto-jumped to when this fleet settles with "needs attention". Preference
 * order (matching the user's mental model of severity):
 *   1. failed
 *   2. defunct (lost-after-restart) — the worker is gone, the user needs
 *      to know whether to respawn or accept the gap
 *   3. cancelled — last because cancellation is operator-driven; the
 *      operator already knows it happened, the jump is for the audit log
 *
 * Returns undefined when the fleet has no failed/stalled children — callers
 * only auto-jump when this returns a card.
 */
export function pickFleetFailedChild(cards: ChatSubagentCard[]): ChatSubagentCard | undefined {
  const failed = cards.find((c) => resolveSubagentActivityPhase(c) === "failed");
  if (failed) return failed;
  const defunct = cards.find((c) => resolveSubagentActivityPhase(c) === "defunct");
  if (defunct) return defunct;
  const cancelled = cards.find((c) => resolveSubagentActivityPhase(c) === "cancelled");
  return cancelled;
}

export function evaluateFleetCompletionNotice(host: FleetCompletionNoticeHost): {
  shouldInject: boolean;
  fingerprint?: string;
  message?: string;
} {
  if (!isChatExecutionIdle(host)) {
    return { shouldInject: false };
  }
  const runCards = filterSubagentRunCards(host.chatSubagents);
  if (runCards.length === 0) {
    return { shouldInject: false };
  }
  const summary = summarizeSubagentFleet(runCards);
  if (summary.overall === "in_progress" || summary.overall === "empty") {
    return { shouldInject: false };
  }
  if (
    shouldSkipFleetCompletionForOrchestration(host.chatSubagents, host.orchestrationReportsDone)
  ) {
    return { shouldInject: false };
  }
  const sessionKey = host.sessionKey.trim();
  const fingerprint = fleetCompletionFingerprint(host.chatSubagents);
  if (!fingerprint) {
    return { shouldInject: false };
  }
  if (host.fleetCompletionNotifiedFingerprint[sessionKey] === fingerprint) {
    return { shouldInject: false };
  }
  if (hasFleetCompletionInTranscript(host.chatMessages)) {
    return { shouldInject: false };
  }
  return {
    shouldInject: true,
    fingerprint,
    message: buildFleetCompletionNoticeMessage(host.chatSubagents),
  };
}

export function stripFleetCompleteLabel(text: string): string {
  return (text ?? "").replace(/^\[(?:fleet-complete|synthesis)\]\s*/i, "").trim();
}

/**
 * Structured final state of a fleet/plan completion bubble.
 *
 * The synthesis inject used to be one opaque prose blob: the renderer lifted
 * the first line into a headline and dumped the rest as monospace body. That
 * produced the operator-reported "no real final state" — no status counts, no
 * per-unit outcome, no merged/failed indicator, just a run-on paragraph.
 *
 * `parseFleetCompletionBody` turns that blob into the sections the card
 * actually renders. It is deliberately tolerant: an unknown/older shape still
 * yields a valid object with everything in `notes`, so a notice written by a
 * previous build never renders as an empty card.
 */
export interface FleetCompletionState {
  /** Machine-readable counts emitted by the gateway, when present. */
  planId?: string;
  done?: number;
  failed?: number;
  cancelled?: number;
  merged?: "merged" | "conflict" | "skipped" | "none";
  /** One-line plan goal, already truncated + secret-scrubbed gateway-side. */
  goal?: string;
  /** Paste-ready prose lifted out of a unit handoff. The operator's answer. */
  answers: string[];
  /** Per-unit result sentences that carried new information. */
  findings: string[];
  /** Unit artifact paths (first per unit). */
  artifacts: string[];
  /** Titles of units that failed / were cancelled. */
  failedTitles: string[];
  cancelledTitles: string[];
  /** Anything the parser did not recognise, so nothing is silently dropped. */
  notes: string[];
}

/** Extract the gateway-emitted `<!-- fleet-synthesis-status: {...} -->` line. */
function parseStatusMarker(body: string): Partial<FleetCompletionState> {
  const m = body.match(/<!--\s*fleet-synthesis-status:\s*(\{.*?\})\s*-->/);
  if (!m) return {};
  try {
    const raw = JSON.parse(m[1]) as Record<string, unknown>;
    const num = (v: unknown): number | undefined =>
      typeof v === "number" && Number.isFinite(v) ? v : undefined;
    const merged = raw.merged;
    return {
      planId: typeof raw.planId === "string" ? raw.planId : undefined,
      done: num(raw.done),
      failed: num(raw.failed),
      cancelled: num(raw.cancelled),
      merged:
        merged === "merged" || merged === "conflict" || merged === "skipped" || merged === "none"
          ? merged
          : undefined,
    };
  } catch {
    // Malformed marker: fall through to prose scraping rather than throwing
    // inside a render path.
    return {};
  }
}

/** Strip every HTML-comment control marker so it never reaches the DOM. */
function stripControlMarkers(body: string): string {
  return body
    .replace(/<!--\s*fleet-synthesis-status:[\s\S]*?-->/g, "")
    .replace(/<!--\s*fleet-complete-synthesis-id:[^>]*?-->/g, "")
    .trim();
}

/** Collect `- item` bullets from a `Section:\n- a\n- b` block. */
function collectBullets(lines: string[], start: number): { items: string[]; next: number } {
  const items: string[] = [];
  let i = start;
  for (; i < lines.length; i += 1) {
    const t = lines[i]?.trim() ?? "";
    if (!t) {
      // A blank line ends the block unless the next line is another bullet.
      const next = lines[i + 1]?.trim() ?? "";
      if (!next.startsWith("- ")) break;
      continue;
    }
    if (!t.startsWith("- ")) break;
    items.push(t.slice(2).trim());
  }
  return { items, next: i };
}

/**
 * Parse a completion/synthesis body into its component sections.
 *
 * Recognised section headers: `Goal:`, `What the units found:`, `Artifacts:`,
 * `Failed units:`, `Cancelled units:`. Section header matching is
 * case-insensitive and tolerates a leading `[synthesis]` marker on the line.
 */
export function parseFleetCompletionBody(body: string): FleetCompletionState {
  const status = parseStatusMarker(body ?? "");
  const cleaned = stripControlMarkers(body ?? "");
  const lines = cleaned.split(/\r?\n/);

  const state: FleetCompletionState = {
    ...status,
    findings: [],
    answers: [],
    artifacts: [],
    failedTitles: [],
    cancelledTitles: [],
    notes: [],
  };

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i] ?? "";
    const line = raw.replace(/^\[synthesis\]\s*/i, "").trim();
    if (!line) continue;

    const header = (name: string) => line.toLowerCase().startsWith(`${name.toLowerCase()}:`);
    const inlineValue = (name: string) => line.slice(name.length + 1).trim() || undefined;

    if (header("Goal")) {
      const inline = inlineValue("Goal");
      if (inline) {
        state.goal = inline;
      } else {
        // Multi-line goal: absorb following non-bullet lines.
        const buf: string[] = [];
        let j = i + 1;
        for (; j < lines.length; j += 1) {
          const t = (lines[j] ?? "").replace(/^\[synthesis\]\s*/i, "").trim();
          if (!t) break;
          if (
            /^(Answer|What the units found|Artifacts|Failed units|Cancelled units|Note)s?:/i.test(t)
          )
            break;
          buf.push(t);
        }
        if (buf.length) {
          state.goal = buf.join(" ").trim();
          i = j - 1;
        }
      }
      continue;
    }

    if (header("Answer")) {
      const inline = inlineValue("Answer");
      const buf: string[] = [];
      if (inline) buf.push(inline);
      let j = i + 1;
      for (; j < lines.length; j += 1) {
        const t = (lines[j] ?? "").replace(/^\[synthesis\]\s*/i, "").trim();
        if (!t) {
          if (buf.length > 0) buf.push("");
          continue;
        }
        if (
          /^(Goal|What the units found|Artifacts|Failed units|Cancelled units|Note)s?:/i.test(t)
        ) {
          break;
        }
        buf.push(t.replace(/^>\s?/, ""));
      }
      const paragraph = buf
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      if (paragraph) state.answers.push(paragraph);
      i = j - 1;
      continue;
    }

    if (header("What the units found")) {
      const inline = inlineValue("What the units found");
      if (inline) {
        state.findings.push(inline);
      } else {
        const { items, next } = collectBullets(lines, i + 1);
        state.findings.push(...items);
        i = next - 1;
      }
      continue;
    }

    if (header("Artifacts")) {
      const inline = inlineValue("Artifacts");
      if (inline) {
        state.artifacts.push(inline);
      } else {
        const { items, next } = collectBullets(lines, i + 1);
        state.artifacts.push(...items);
        i = next - 1;
      }
      continue;
    }

    if (header("Failed units")) {
      const inline = inlineValue("Failed units");
      if (inline) {
        state.failedTitles.push(inline);
      } else {
        const { items, next } = collectBullets(lines, i + 1);
        state.failedTitles.push(...items);
        i = next - 1;
      }
      continue;
    }

    if (header("Cancelled units")) {
      const inline = inlineValue("Cancelled units");
      if (inline) {
        state.cancelledTitles.push(inline);
      } else {
        const { items, next } = collectBullets(lines, i + 1);
        state.cancelledTitles.push(...items);
        i = next - 1;
      }
      continue;
    }

    // The headline itself and the trailing "Read any one with …" hint are
    // chrome, not findings — they are rendered by the card itself.
    if (line.startsWith("Read any one with")) continue;
    if (line.startsWith("[synthesis] Merge conflict")) {
      state.notes.push(line);
      continue;
    }
    if (/^Work plan \S+ complete/i.test(line)) continue;

    state.notes.push(line);
  }

  return state;
}

export function fleetCompleteHeadline(body: string): string {
  const first =
    (body ?? "")
      .split(/\r?\n/)
      .find((line) => line.trim() && !line.trim().startsWith("<!--"))
      ?.trim() ?? "";
  return (
    first
      .replace(/^\*\*|\*\*$/g, "")
      .replace(/^\[synthesis\]\s*/i, "")
      .trim() || "Fleet complete"
  );
}

/**
 * Drop the first non-empty line of `body` when it matches `headline`.
 *
 * The completion notice lifts the headline into a styled header row, so
 * leaving the same line at the top of the body renders the same sentence
 * twice (header + first body paragraph) and the bubble reads as a
 * duplicated, unfinished run-on. Match the headline case-insensitively
 * and tolerating the `**` bold-wrap that the fleet-summary headline uses
 * (`**All tasks complete**` vs `All tasks complete`). Lines that merely
 * contain the headline are NOT dropped — only the line that *is* the
 * headline. Whitespace-only lines around the headline are also trimmed
 * so the body does not open with a blank paragraph.
 */
export function stripFirstMatchingLine(body: string, headline: string): string {
  const src = body ?? "";
  const needle = (headline ?? "").trim();
  if (!needle) return src;
  const lines = src.split(/\r?\n/);
  let dropped = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i]?.trim() ?? "";
    if (!trimmed) continue;
    const normalised = trimmed.replace(/^\*\*|\*\*$/g, "").trim();
    if (normalised.toLowerCase() === needle.toLowerCase()) {
      dropped = i;
    }
    break;
  }
  if (dropped < 0) return src;
  // Also drop a single blank line that follows the headline so the body
  // does not open with a visually-empty paragraph.
  const next = lines[dropped + 1];
  const end = next !== undefined && next.trim() === "" ? dropped + 2 : dropped + 1;
  const out = lines.slice(end).join("\n");
  return out;
}

export function clearFleetCompletionNoticeForSession(
  notified: Record<string, string>,
  sessionKey: string,
): Record<string, string> {
  const key = sessionKey.trim();
  if (!notified[key]) {
    return notified;
  }
  const next = { ...notified };
  delete next[key];
  return next;
}
